import log from 'loglevel';

/**
 * Environment Configuration Constants
 *
 * Provides utilities for reading environment variables in both
 * Vite (browser) and Node (Jest/Lambda) contexts.
 *
 * IMPORTANT:
 * - In Vite (browser), environment variables are accessed via import.meta.env
 *   and only variables prefixed with VITE_ are exposed to the client.
 * - In Node (Jest/Lambda), environment variables are accessed via process.env.
 *
 * S3 bucket/path configuration is owned entirely by the Lambda.
 * The frontend only needs VITE_FILE_MANAGER_MAX_UPLOAD_BYTES for client-side
 * file size validation before upload.
 */

/**
 * Environment variable names used by the File Manager frontend.
 *
 * Bucket names and paths are NOT included here — those are Lambda-side config
 * set via Amplify environment variables (FILE_MANAGER_BUCKET_NAME, etc.).
 */
export const ENV_VARS = {
  /** Max upload size in bytes — must match Lambda's FILE_MANAGER_MAX_UPLOAD_BYTES */
  FILE_MANAGER_MAX_UPLOAD_BYTES: 'VITE_FILE_MANAGER_MAX_UPLOAD_BYTES',

  NODE_ENV: 'NODE_ENV',
} as const;

/**
 * Read an environment variable from Vite (browser) or Node (process.env).
 *
 * Notes:
 * - We intentionally avoid referencing the global identifier `process` directly.
 *   In the browser, `process` is undefined and would throw a ReferenceError if used.
 */
export function readEnvValue(envKey: string): string | undefined {
  // 1) Vite/browser environment: import.meta.env
  let viteEnv: Record<string, unknown> | undefined;

  const g = globalThis as unknown as {
    __VITE_ENV__?: Record<string, unknown>;
  };
  if (typeof g.__VITE_ENV__ !== 'undefined') {
    viteEnv = g.__VITE_ENV__;
  }

  // Special-case NODE_ENV on Vite: MODE is the canonical value
  if (envKey === 'NODE_ENV' && viteEnv && typeof viteEnv.MODE === 'string') {
    return viteEnv.MODE;
  }

  if (viteEnv && Object.prototype.hasOwnProperty.call(viteEnv, envKey)) {
    const v = viteEnv[envKey];
    if (typeof v === 'string') return v;
    if (v != null) return String(v);
  }

  // 2) Node environment: process.env (accessed safely via globalThis)
  const p = (
    globalThis as unknown as {
      process?: { env?: Record<string, string | undefined> };
    }
  ).process;
  const nodeEnv = p?.env;

  const n = nodeEnv?.[envKey];
  return typeof n === 'string' ? n : undefined;
}

/**
 * Get numeric environment variable with validation
 */
export function getNumericEnvVar(
  key: keyof typeof ENV_VARS,
  defaultValue: number,
  min?: number,
  max?: number
): number {
  const envKey = ENV_VARS[key];
  const stringValue = readEnvValue(envKey);
  if (!stringValue) {
    return defaultValue;
  }

  const numericValue = Number(stringValue);
  if (Number.isNaN(numericValue)) {
    log.warn(
      `Environment variable ${envKey} is not a valid number: ${stringValue}, using default: ${defaultValue}`
    );
    return defaultValue;
  }

  if (min !== undefined && numericValue < min) {
    log.warn(
      `Environment variable ${envKey} is below minimum ${min}: ${numericValue}, using minimum`
    );
    return min;
  }

  if (max !== undefined && numericValue > max) {
    log.warn(
      `Environment variable ${envKey} is above maximum ${max}: ${numericValue}, using maximum`
    );
    return max;
  }

  return numericValue;
}
